import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/RegistrationsService';
import {Registration} from '../types';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';

export const useRegistration = (registrationId?: string) => {
    const queryClient = useQueryClient();

    const registrationQuery = useQuery({
        queryKey: ['registration', registrationId],
        queryFn: () => fetch(registrationId!),
        enabled: !!registrationId
    });

    const createMutation = useMutation({
        mutationFn: (values: Registration) => {
            return add(stripReadonlyFields(values));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['registrations']});
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: Registration) => {
            if (!registrationId) throw new Error('registrationId is required parameter');
            return update(registrationId, stripReadonlyFields(values) as Registration);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['registrations']});
            queryClient.invalidateQueries({queryKey: ['registration', registrationId]});
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!registrationId) throw new Error('registrationId is required parameter');
            return deleteRecord(registrationId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['registrations']});
        }
    });

    return {
        record: registrationQuery.data,
        loading: registrationQuery.isLoading,
        fetchRecord: registrationQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
